/**
 * @file PaytableConfig.ts
 * @role Loads + validates the paytable players are shown. Every key must be a real RGS symbol
 *       id, so a fictional row fails at boot instead of being rendered to a player. Unconfirmed
 *       match lengths are ABSENT rather than guessed — the panel renders those as an em dash.
 * @owns The symbol -> match length -> bet multiplier table and its provenance.
 * @reads Bundled paytable.config.json (default) or injected raw config (tests), plus the list of
 *        known symbol ids it validates against (passed in — core never reaches into game/).
 * @neverDoes Decide or compute a win (the RGS does). Invent a payout. Render.
 * @invariants 10 (validate before use), 1 (display only — never a source of win amounts).
 * @see docs/CONFIG_GUIDE.md, docs/RGS_SERVER_REQUIREMENTS.md
 */
import { z } from 'zod';
import paytableConfigRaw from '@config/paytable.config.json';
import { GameError } from '@core/errors/GameError';

/** Match lengths a 5-reel left-to-right line can pay on. */
export const MATCH_LENGTHS = [5, 4, 3] as const;
export type MatchLength = (typeof MATCH_LENGTHS)[number];

const multiplier = z.number().positive().finite();

const PaytableConfigSchema = z.object({
  /**
   * `measured` — sampled from the live RGS, so the panel must say so. `server` — supplied by
   * the server itself, at which point this file is only a cache.
   */
  source: z.enum(['measured', 'server']),
  /** ISO date the numbers were established; shown with the provisional note. */
  measuredAt: z.string().min(1),
  payouts: z.record(
    z.string().min(1),
    z
      .object({ 3: multiplier.optional(), 4: multiplier.optional(), 5: multiplier.optional() })
      .strict(),
  ),
});

export type PaytableConfigData = z.infer<typeof PaytableConfigSchema>;
export type SymbolPayouts = Readonly<Record<MatchLength, number | undefined>>;

/** The bundled table; callers pass it explicitly so this module stays dependency-free. */
export const bundledPaytableConfig: unknown = paytableConfigRaw;

export class PaytableConfig {
  readonly source: PaytableConfigData['source'];
  readonly measuredAt: string;
  private readonly payouts: Map<string, SymbolPayouts>;

  constructor(raw: unknown, knownSymbolIds: readonly string[]) {
    const parsed = PaytableConfigSchema.safeParse(raw);
    if (!parsed.success) {
      throw new GameError(
        'CONFIG_LOAD_FAILED',
        `Invalid paytable config: ${parsed.error.message}`,
        {
          cause: parsed.error,
        },
      );
    }

    // The whole point of this file: a payout keyed by a symbol the server has never heard of is
    // a fabricated row. Fail the build rather than show it to a player.
    const known = new Set(knownSymbolIds);
    const unknown = Object.keys(parsed.data.payouts).filter((id) => !known.has(id));
    if (unknown.length > 0) {
      throw new GameError(
        'CONFIG_LOAD_FAILED',
        `Paytable references symbols that are not in the registry: ${unknown.join(', ')}.`,
      );
    }

    this.source = parsed.data.source;
    this.measuredAt = parsed.data.measuredAt;
    this.payouts = new Map(
      Object.entries(parsed.data.payouts).map(([id, entry]) => [
        id,
        { 3: entry[3], 4: entry[4], 5: entry[5] },
      ]),
    );
  }

  /** Confirmed multipliers for a symbol. Absent lengths stay undefined — never defaulted. */
  forSymbol(symbolId: string): SymbolPayouts {
    return this.payouts.get(symbolId) ?? { 3: undefined, 4: undefined, 5: undefined };
  }

  /** True while the numbers come from sampling rather than the server (drives the UI note). */
  get isProvisional(): boolean {
    return this.source === 'measured';
  }
}
